import {Injectable} from '@angular/core';
import {DOCUMENT_SENDER_ENTITY_LABEL_PAIR} from '../../interfaces/imetadata';
import {ISignatureFieldMeta, SignatureField} from '../../interfaces/signature';
import {DateFormatOptions} from '../../utils/fieldconfig';
import {templateContext} from '../../utils/template-context';
import {UidGenerator} from '../../utils/uidGenerator';
import {DocusignOptionsService, PropertiesKeys} from '../esignature/docusign/options/docusign-options.service';
import {DocumentSenderFieldProviderService} from '../field-selection/document-sender/document-sender-field-provider.service';
import {AdcMigrationProcessedFieldInfo, AdcMigrationStatus} from './models';

interface IFieldProcessor {
  process: (groups: { [p: string]: string }) => AdcMigrationProcessedFieldInfo;
}

@Injectable({ providedIn: 'root' })
export class FieldExpressionProcessorService {
  private static ElvisAndFormattedDateAndQuotedDefault = /^\$\{(?<path>[\w.]+)\.format\(["'](?<format>.*)["']\)\s*\?:\s*["'](?<default>.*)["']}$/;
  private static TernaryAndFormattedDateAndQuotedDefault =
    /^\$\{(?<path>[\w.]+)\s*\?\s*([\w.]+)\.format\(["'](?<format>.*)["']\)\s*:\s*["'](?<default>.*)["']}$/;
  private static SingleFormattedDate = /^\$\{(?<path>[\w.]+)\.format\(["'](?<format>.*)["']\)\s*}$/;
  private static ElvisAndQuotedDefault = /^\$\{(?<path>[\w.]+)\s*\?:\s*["'](?<default>.*?)["']}$/;
  private static TernaryAndQuotedDefault = /^\${(?<path>[\w.]+)\s*\?\s*([\w.]+)\s*:\s*["'](?<default>.*)["']}$/;
  private static SingleField = /^\$\{(?<path>[\w.]+)}$/;
  private static DocusignField = /^\$\{__(?<path>DOCUSIGN\.[a-zA-Z_]+)(?<recipientNo>\d+)(?<opt>_o)?__}$/;
  private static DocusignDSField = /^\$\{__(?<path>DS\.[a-zA-Z_]+)(?<recipientNo>\d+)-[a-zA-Z]+(?<opt>_o)?__}$/;
  private static expressionMatchers = [
    FieldExpressionProcessorService.DocusignField,
    FieldExpressionProcessorService.DocusignDSField,
    FieldExpressionProcessorService.ElvisAndFormattedDateAndQuotedDefault,
    FieldExpressionProcessorService.TernaryAndFormattedDateAndQuotedDefault,
    FieldExpressionProcessorService.SingleFormattedDate,
    FieldExpressionProcessorService.ElvisAndQuotedDefault,
    FieldExpressionProcessorService.TernaryAndQuotedDefault,
    FieldExpressionProcessorService.SingleField,
  ];

  constructor(private documentSenderFieldProviderService: DocumentSenderFieldProviderService) { }

  public process(expression: string): AdcMigrationProcessedFieldInfo {
    expression = this.normaliseExpression(expression);

    const groups = FieldExpressionProcessorService.expressionMatchers
      .map((regex) => expression.match(regex))
      .find((match) => match !== null)?.groups;

    if (!groups) {
      return { description: 'Expression not recognised.', status: AdcMigrationStatus.NOT_RECOGNISED };
    }

    return this.fieldProcessorFactory(groups.path).process(groups);
  }

  private normaliseExpression(expr: string): string {
    const trimQuotes = (str) => str.replace(/^"|"$/g, '');
    const missingExpressionSyntax = (str) => !(str.startsWith(`\${`) && str.endsWith('}'));

    expr = trimQuotes(expr.trim());
    if (missingExpressionSyntax(expr)) {
      expr = `\${${expr}}`;
    }

    // expr = expr.replaceAll(/\\"\\"/g, '""');
    expr = expr.replaceAll(/\\"/g, '"');

    return expr;
  }

  private corporateUserFieldProcessor(groups:  { [p: string]: string }): AdcMigrationProcessedFieldInfo {
    const currentUserFieldPath = 'currentUser' + groups.path.substring('corporateUser'.length);
    const currentUserFieldMeta = this.documentSenderFieldProviderService.lookup(currentUserFieldPath);
    if (currentUserFieldMeta) {
      return {
        meta: {entity: DOCUMENT_SENDER_ENTITY_LABEL_PAIR, meta: currentUserFieldMeta},
        description: '',
        status: AdcMigrationStatus.MIGRATABLE,
      };
    }

    return {
      meta: {entity: DOCUMENT_SENDER_ENTITY_LABEL_PAIR},
      description: 'No such field for current user.',
      status: AdcMigrationStatus.NO_SUCH_FIELD,
    };
  }

  private dataFieldProcessor(groups:  { [p: string]: string }): AdcMigrationProcessedFieldInfo {
    const zeroWidthSpace = '\u200B';

    const amendPathForTargetEntity = (srcPath: string) => {
      const targetEntity = templateContext.getTargetEntity().entity;
      if (srcPath.toLowerCase().startsWith(targetEntity.toLowerCase())) {
        srcPath = srcPath.substring(targetEntity.length + 1);   // remove the target entity from the start of the path
      }
      return targetEntity + '.' + srcPath;                      // put the target entity at the start with the correct case
    };

    const fieldPath = amendPathForTargetEntity(groups.path);
    const fieldMeta = templateContext.getFieldByPath(fieldPath);
    if (!fieldMeta) {
      return {
        description: 'Expression matched, but no such field found.',
        status: AdcMigrationStatus.NO_SUCH_FIELD
      };
    }

    // TODO: check the meta is it a date type
    let dateOptions: DateFormatOptions | undefined;
    if (groups.format) {
      dateOptions = {dateFormat: groups.format, language: ''}; // assume en-GB?
    }

    return {
      meta: {
        entity: templateContext.getTargetEntity(),
        meta: fieldMeta,
        options: {date: dateOptions, default: groups.default || zeroWidthSpace }
      },
      description: '',
      status: AdcMigrationStatus.MIGRATABLE
    };
  }

  private docusignFieldProcessor(groups:  { [p: string]: string }): AdcMigrationProcessedFieldInfo {
    if (groups.path.startsWith('DS.')) {
      return {description: 'Docusign field not supported.', status: AdcMigrationStatus.NOT_SUPPORTED};
    }

    // starts with DOCUSIGN

    const buildTabLabel = (fieldType: string): string => {
      const parsedType = fieldType.replaceAll(/\W/g, '_');
      return `${parsedType}_${UidGenerator.createGuid()}`;
    };

    // look at docusign-options.service.ts - FieldType
    const adcFieldTypeToAdv2Map: { [p: string]: string } = {
      RECIPIENT: 'signature',
      text: 'text',
      NAME: 'name',
      TITLE: 'title',
      DATE: 'date-signed'   // date-signed OR date?
    };

    const adcFieldType = groups.path.substring('DOCUSIGN.'.length);
    const signatureFieldType = adcFieldTypeToAdv2Map[adcFieldType];

    if (!signatureFieldType) {
      return {description: 'Docusign field type not recognised.', status: AdcMigrationStatus.NOT_RECOGNISED};
    }

    const capitalise = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

    const field: ISignatureFieldMeta = {
      type: signatureFieldType,
      label: `${capitalise(signatureFieldType)}`,
      isParent: false
    };

    const properties: any = {
      [PropertiesKeys.RECIPIENT_NO]: parseInt(groups.recipientNo, 10),
      [PropertiesKeys.TAB_LABEL]: buildTabLabel(field.type)
    };

    const signatureField: SignatureField = {
      ...field,
      id: groups.recipientNo,
      properties: properties
    };

    DocusignOptionsService.addDefaultValues(signatureField);

    return {
      description: '',
      status: AdcMigrationStatus.MIGRATABLE,
      meta: signatureField
    };
  }

  private fieldProcessorFactory(path: string): IFieldProcessor {
    if (path.startsWith('corporateUser')) {
      return {process: this.corporateUserFieldProcessor.bind(this)};
    }

    if (path.startsWith('DOCUSIGN.') || path.startsWith('DS.')) {
      return {process: this.docusignFieldProcessor.bind(this)};
    }

    return {process: this.dataFieldProcessor.bind(this)};
  }
}
